import React, {Component, ReactElement, ReactNode} from 'react'

import NatRoomToResidentRepo, {GetRoomToResidentParams} from '../roomToResident/repository/NatRoomToResidentRepo'
import {NatGetRoomToResident, ResidentUserInfo} from '../roomToResident/repository/NatGetRoomToResident'
import {NatGetCondominiumGroup} from '../condominiumGroup/repository/NatGetCondominiumGroup'
import NatCondominiumGroupRepo from '../condominiumGroup/repository/NatCondominiumGroupRepo'
import NatRidgeRepo from '../ridge/repository/NatRidgeRepo'
import {ADMINISTORATOR_MODE, RESIDENT_STATUS, ROLE_FOR_RESIDENT} from '../../../CodeMaster'
import {NatGetRidge} from '../ridge/repository/NatGetRidge'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import ListSearchFormArea from '../../../styledComponents/adminPage/ListSearchFormArea'
import {
    ConditionButtonCell,
    ConditionCategoryCell,
    ConditionColumNameCell,
    ConditionInputCell,
    ConditionTable,
    ConditionTableBody,
    ConditionTableRow,
} from '../../../styledComponents/adminPage/ConditionTable'
import PPTextField from '../../../components/shared/forms/PPTextField'
import {AdminDefaultMediumButton} from '../../../styledComponents/adminPage/Buttons'
import SearchPageActionArea from '../../../styledComponents/adminPage/SearchPageActionArea'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {PageMainArea} from '../../../components/shared/hoc/withAdminPageTemplate'
import {SearchResultCountDisplayProps} from '../../../styledComponents/adminPage/SearchResultCountDisplay'
import {Messages} from '../../../Messages'
import {HKConstants} from '../../../HKConstants'
import {COMMON_CONSTANTS} from '../../../CommonConstants'
import {SearchResult} from '../adminUser/repository/NatGetAdminUserList'
import SortableTableHeader, {
    SortableTableHeaderCell,
} from '../../../styledComponents/adminPage/SortablePaginationTableHeader'
import {SortDirection} from '@mui/material/TableCell'
import {formatDateTimeForUpdateDate} from '../../../formatters/formatDate'
import PPCheckboxGroup from '../../../components/shared/forms/PPCheckboxGroup'
import PPSelect, {OptionProps} from '../../../components/shared/forms/PPSelect'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import NatCondominiumGroupSelectComponent from '../common/NatCondominiumGroupSelectComponent'
import styled from 'styled-components'
import {Box, Link, Table, TableBody, TableCell, TableRow} from '@mui/material'
import {BoldSpan} from '../../../styledComponents/StyledSpan'
import {StyledDiv} from '../../../styledComponents/StyledTypeDefinitions'
import {LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import {SortOrder} from '../../../components/shared/SortOrder'
import {needsMaskConfidentialDataByLoginUser} from '../../common/util/Natutil'
import {SelectChangeEvent} from '@mui/material/Select'
import {Location} from 'react-router-dom'

const KEY_SEARCH_CONDITION: string = 'adminResidentUserListPageSearchCondition'
const KEY_LOGIN_USER_CONDOMINIUM_GROUP_ID: string = 'loginUserCondominiumGroupId'
const KEY_SELECTED_CONDOMINIUM_CD: string = 'selectedCondominiumCd'
const SELECTED_ADMINISTORATOR_MODE: string = 'selectedMode'

interface Props {
    RoomToResidentRepo: NatRoomToResidentRepo
    CondominiumGroupRepo: NatCondominiumGroupRepo
    RidgeRepo: NatRidgeRepo
    eventEmitter: EventEmitterExAdmin
    location: Location

}

interface States {
    condominiumGroup: NatGetCondominiumGroup
    ridge: NatGetRidge
    residentUsers: ResidentUserInfo[]
    searchCondition: SearchCondition
    searchConditionSortInfo: SortOrder[]
    searchResult: SearchResult
    defaultRole: CheckBoxData[]
    defaultStatus: CheckBoxData[]
    selectedCondominiumCd: string | null
    dispResultMessage: boolean
    loginUserCondominiumGroupId: number | null
    isLoadCompleted: boolean
}

export interface CheckBoxData {
    id: string
    label: string
    disp: boolean
}

export interface SearchCondition {
    ridgeCd?: string
    roomNumber?: string
    roleId?: string
    familyName?: string
    firstName?: string
    familyNameKana?: string
    firstNameKana?: string
    mailAddress?: string
    status?: string
    condominiumGroupId?: number | null
    condominiumCd?: string
    condominiumName?: string
}


class NatAdminResidentUserListPage extends Component<Props & AdminPagePropsSet, States> {
    administratorMode: Nullable<string>

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        this.administratorMode = SessionStorageHelper.getStringItemOrNull(SELECTED_ADMINISTORATOR_MODE)

        const selectedCondominiumCd: Nullable<string> =
            SessionStorageHelper.getStringItemOrNull(KEY_SELECTED_CONDOMINIUM_CD)

        const loginUserCondominiumGroupId: Nullable<number> =
            SessionStorageHelper.getNumberItemOrNull(KEY_LOGIN_USER_CONDOMINIUM_GROUP_ID)

        let defaultSearchConditionSortInfo: SortOrder[] = []
        if (this.administratorMode === ADMINISTORATOR_MODE.ALL.value) {
            defaultSearchConditionSortInfo = [
                new SortOrder('updateDatetime', 'desc'),
                new SortOrder('condominiumGroupName', 'asc'),
                new SortOrder('condominiumName', 'asc'),
                new SortOrder('ridgeName', 'asc'),
                new SortOrder('roomNumber', 'asc'),
            ]
        } else {
            defaultSearchConditionSortInfo = [
                new SortOrder('updateDatetime', 'desc'),
                new SortOrder('ridgeName', 'asc'),
                new SortOrder('roomNumber', 'asc'),
            ]
        }

        // 検索条件の初期設定
        let defaultSearchCondition: SearchCondition = {}

        // 前回検索条件が保持されていればデフォルト値として設定する
        const sessionStorageSearchCondition: string | null = window.sessionStorage.getItem(KEY_SEARCH_CONDITION)

        if (sessionStorageSearchCondition) {
            defaultSearchCondition = JSON.parse(sessionStorageSearchCondition)
        } else {
            // 前回検索条件が設定されていない場合、ステータスはすべてチェック状態とする
            const defaultStatus: string[] = []
            RESIDENT_STATUS.values().forEach((status) => {
                if (RESIDENT_STATUS.HASEKO_WITHDRAW.value !== status.value) {
                    defaultStatus.push(status.value)
                }
            })
            defaultSearchCondition.status = defaultStatus.join(',')
            defaultSearchCondition.roleId = ROLE_FOR_RESIDENT.values().map((master) => master.value).join(',')

            // 全体モードの場合
            if (this.administratorMode === ADMINISTORATOR_MODE.ALL.value) {
                if (loginUserCondominiumGroupId) {
                    defaultSearchCondition.condominiumGroupId = loginUserCondominiumGroupId
                }
            }
        }

        this.state = {
            condominiumGroup: {
                ['details']: null,
                ['informations']: [],
                ['requestError']: null,
                ['requestItemErrors']: [],
            },
            ridge: {
                ['details']: [],
                ['informations']: [],
                ['requestError']: null,
                ['requestItemErrors']: [],
            },
            residentUsers: [],
            searchCondition: defaultSearchCondition,
            searchConditionSortInfo: defaultSearchConditionSortInfo,
            defaultRole: [],
            defaultStatus: [],
            selectedCondominiumCd: selectedCondominiumCd,
            searchResult: {},
            dispResultMessage: false,
            loginUserCondominiumGroupId: loginUserCondominiumGroupId,
            isLoadCompleted: false,
        }

        this.onClickSearchButton = this.onClickSearchButton.bind(this)
        this.registerResidentUser = this.registerResidentUser.bind(this)
        this.bulkUpdateResidentUser = this.bulkUpdateResidentUser.bind(this)
        this.onChangeCondominiumGroup = this.onChangeCondominiumGroup.bind(this)
        this.onChangeCondominiumCd = this.onChangeCondominiumCd.bind(this)
        this.onChangeCondominiumName = this.onChangeCondominiumName.bind(this)
        this.onChangeFamilyName = this.onChangeFamilyName.bind(this)
        this.onChangeFirstName = this.onChangeFirstName.bind(this)
        this.onChangeFamilyNameKana = this.onChangeFamilyNameKana.bind(this)
        this.onChangeFirstNameKana = this.onChangeFirstNameKana.bind(this)
        this.onChangeRoomNumber = this.onChangeRoomNumber.bind(this)
        this.onChangeRidgeCd = this.onChangeRidgeCd.bind(this)
        this.onChangeMailAddress = this.onChangeMailAddress.bind(this)
        this.handleSortResidentUsers = this.handleSortResidentUsers.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {

            if (this.administratorMode === ADMINISTORATOR_MODE.ALL.value) {
                // 物件グループマスタ取得
                const condominiumGroupResponse: Nullable<NatGetCondominiumGroup> = await this.props.asyncCallFunction(
                    this.props.CondominiumGroupRepo.getCondominiumGroup.bind(this.props.CondominiumGroupRepo),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (condominiumGroupResponse) {
                    this.setState({condominiumGroup: condominiumGroupResponse})
                }
            } else {
                // 棟取得
                const ridgeResponse: Nullable<NatGetRidge> = await this.props.asyncCallFunction(
                    this.props.RidgeRepo.getRidgeList.bind(this.props.RidgeRepo, this.state.selectedCondominiumCd ?
                        this.state.selectedCondominiumCd : undefined),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )

                if (ridgeResponse) {
                    this.setState({ridge: ridgeResponse})
                }

            }
            this.showInitialMessage()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }

    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

// 登録ボタン押下
    async registerResidentUser(): Promise<void> {
        this.props.navigate(
            '/admin/residentUserRegister',
        )
    }

    async bulkUpdateResidentUser(): Promise<void> {
        this.props.navigate(
            '/admin/bulkUpdate/residentUser',
        )
    }

    async searchResidentUsers(searchCondition?: SearchCondition,
                              searchConditionSortInfos?: SortOrder[]): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const getRoomToResidentParams: GetRoomToResidentParams = {}
            if (searchCondition) {
                if (searchCondition.ridgeCd) {
                    getRoomToResidentParams.ridgeCd = searchCondition.ridgeCd
                }
                if (searchCondition.roomNumber) {
                    getRoomToResidentParams.roomNumber = searchCondition.roomNumber
                }
                if (searchCondition.roleId) {
                    getRoomToResidentParams.roleId = searchCondition.roleId
                }
                if (searchCondition.familyName) {
                    getRoomToResidentParams.familyName = searchCondition.familyName
                }
                if (searchCondition.firstName) {
                    getRoomToResidentParams.firstName = searchCondition.firstName
                }
                if (searchCondition.familyNameKana) {
                    getRoomToResidentParams.familyNameKana = searchCondition.familyNameKana
                }
                if (searchCondition.firstNameKana) {
                    getRoomToResidentParams.firstNameKana = searchCondition.firstNameKana
                }
                if (searchCondition.mailAddress) {
                    getRoomToResidentParams.mailAddress = searchCondition.mailAddress
                }
                if (searchCondition.status) {
                    getRoomToResidentParams.status = searchCondition.status
                }
                if (searchCondition.condominiumGroupId) {
                    getRoomToResidentParams.condominiumGroupId = `${searchCondition.condominiumGroupId}`
                }
                if (searchCondition.condominiumCd) {
                    getRoomToResidentParams.condominiumCd = searchCondition.condominiumCd
                }
                if (searchCondition.condominiumName) {
                    getRoomToResidentParams.condominiumName = searchCondition.condominiumName
                }
            }


            if (searchConditionSortInfos) {
                const sortInfos: string[] = []
                searchConditionSortInfos.forEach((sortInfo) => {
                    sortInfos.push(`${sortInfo.sortKey}:${sortInfo.sortDirection}`)
                })
                getRoomToResidentParams.sortInfo = sortInfos
            }


            // 居住者ユーザ一覧取得
            const getResidentUsersResponse: Nullable<NatGetRoomToResident> =
                await this.props.asyncCallFunction(
                    this.props.RoomToResidentRepo.getRoomToResident.bind(
                        this.props.RoomToResidentRepo,
                        getRoomToResidentParams,
                    ),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )

            if (getResidentUsersResponse && getResidentUsersResponse.details) {
                this.setState({
                    residentUsers: getResidentUsersResponse.details.residentUserInfo,
                    searchResult: getResidentUsersResponse.details.searchResult,
                })
            }

        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({dispResultMessage: true})
        }
    }

    searchResults(): SearchResultCountDisplayProps {
        if (this.state.dispResultMessage) {
            if (this.state.searchResult.limitOverFlag === 1) {
                return {
                    noResultMessage:
                        Messages.IOPCC0016.toString(
                            HKConstants.VMN.ADM.ADMINISTRATOR_USER_SEARCH_RESULTS_LIMIT,
                        ),
                }
            }
            if (this.state.residentUsers.length === 0) {
                return {noResultMessage: Messages.IOPCC0014.toString('')}
            }
        }

        return {
            totalCount: this.state.searchResult.total ? this.state.searchResult.total : undefined,
            rowsPerPage: COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[2],
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }
    }

    async onClickSearchButton(): Promise<void> {
        this.props.eventEmitter.clearHeadingAreaMessage()
        await this.searchResidentUsers(this.state.searchCondition, this.state.searchConditionSortInfo)
        // 検索条件を保持
        window.sessionStorage.setItem(KEY_SEARCH_CONDITION, JSON.stringify(this.state.searchCondition))
    }

    async handleSortResidentUsers(sortKey: string): Promise<void> {

        let sortDirection: SortDirection = this.getSortDirection(sortKey)
        if (sortDirection === false || sortDirection === 'desc') {
            sortDirection = 'asc'
        } else {
            sortDirection = 'desc'
        }

        let sortOrders: SortOrder[] = []
        if (sortKey === 'roomNumber') {
            sortOrders = [
                new SortOrder('ridgeName', sortDirection),
                new SortOrder(sortKey, sortDirection),
            ]
        } else {
            sortOrders = [new SortOrder(sortKey, sortDirection)]
        }
        await this.searchResidentUsers(
            this.state.searchCondition,
            sortOrders,
        )
        this.setState({searchConditionSortInfo: sortOrders})
    }

    getSortDirection(sortKey: string): SortDirection {
        const sortOrder: SortOrder | undefined =
            this.state.searchConditionSortInfo.find(order => order.sortKey === sortKey)
        if (sortOrder === undefined) {
            return false
        }
        return sortOrder.sortDirection
    }

    renderSearchConditionCondominiumInfo(): Nullable<ReactElement> {
        if (this.administratorMode === ADMINISTORATOR_MODE.ALL.value) {
            // 全体モード
            return (
                <>
                    <ConditionTableRow>
                        <ConditionCategoryCell rowSpan={3}>物件情報</ConditionCategoryCell>
                        <ConditionColumNameCell>物件グループを選択</ConditionColumNameCell>
                        <ConditionInputCell colSpan={2}>
                            {
                                this.state.condominiumGroup &&
                                this.state.condominiumGroup.details &&
                                <>
                                    <NatCondominiumGroupSelectComponent
                                        condominiumGroup={this.state.condominiumGroup.details}
                                        onChangeSelectedCondominiumGroupId=
                                            {this.onChangeCondominiumGroup}
                                        defaultValue={(this.state.searchCondition.condominiumGroupId) ?
                                            this.state.searchCondition.condominiumGroupId : null}
                                        requiredFlag={true}
                                        eventKeys={['condominiumGroupId']}
                                        eventEmitter={this.props.eventEmitter}
                                        autoFocus={true}
                                    >
                                    </NatCondominiumGroupSelectComponent>
                                </>
                            }
                        </ConditionInputCell>
                        <ConditionButtonCell></ConditionButtonCell>
                    </ConditionTableRow>
                    <ConditionTableRow>
                        <ConditionColumNameCell>物件コード</ConditionColumNameCell>
                        <ConditionInputCell>
                            <PPTextField
                                InputProps={{
                                    inputProps: {
                                        maxLength: 6,
                                    },
                                }}
                                fullWidth
                                onChange={this.onChangeCondominiumCd}
                                defaultValue={this.state.searchCondition.condominiumCd}
                            />
                        </ConditionInputCell>
                    </ConditionTableRow>
                    <ConditionTableRow>
                        <ConditionColumNameCell>物件名</ConditionColumNameCell>
                        <ConditionInputCell>
                            <PPTextField
                                InputProps={{
                                    inputProps: {
                                        maxLength: 50,
                                    },
                                }}
                                fullWidth
                                onChange={this.onChangeCondominiumName}
                                defaultValue={this.state.searchCondition.condominiumName}
                            />
                        </ConditionInputCell>
                        <ConditionButtonCell></ConditionButtonCell>
                    </ConditionTableRow>
                </>
            )
        } else {
            // 物件選択モード
            return (
                <>
                    <ConditionTableRow>
                        <ConditionCategoryCell rowSpan={2}>物件情報</ConditionCategoryCell>
                        <ConditionColumNameCell>棟名</ConditionColumNameCell>
                        <ConditionInputCell>
                            <PPSelect
                                fullWidth
                                data={this.getRidgeList()}
                                onChange={this.onChangeRidgeCd.bind(this)}
                                value={this.state.searchCondition.ridgeCd}
                                autoFocus
                            />
                        </ConditionInputCell>
                    </ConditionTableRow>
                    <ConditionTableRow>
                        <ConditionColumNameCell>部屋番号</ConditionColumNameCell>
                        <ConditionInputCell>
                            <PPTextField
                                InputProps={{
                                    inputProps: {
                                        maxLength: 4,
                                    },
                                }}
                                fullWidth
                                onChange={this.onChangeRoomNumber}
                                defaultValue={this.state.searchCondition.roomNumber}
                            />
                        </ConditionInputCell>
                        <ConditionButtonCell></ConditionButtonCell>
                    </ConditionTableRow>
                </>
            )
        }
    }

    renderSearchConditionResidentInfo(): Nullable<ReactElement> {
        return (
            <>
                <ConditionTableRow>
                    <ConditionCategoryCell rowSpan={5}>居住者情報</ConditionCategoryCell>
                    <ConditionColumNameCell>権限</ConditionColumNameCell>
                    <ConditionInputCell colSpan={3}>
                        {this.renderRoleCheckBox()}
                    </ConditionInputCell>
                </ConditionTableRow>
                <ConditionTableRow>
                    <ConditionColumNameCell>氏名</ConditionColumNameCell>
                    <ConditionInputCell>
                        <LeftRowFlexContainer>
                            <FlexContainerColumn>
                                <span>姓</span>
                                <NameWrapper>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 10,
                                            },
                                        }}
                                        fullWidth
                                        onChange={this.onChangeFamilyName}
                                        defaultValue={this.state.searchCondition.familyName}
                                    />
                                </NameWrapper>
                            </FlexContainerColumn>
                            <FlexContainerColumn>
                                <span>名</span>
                                <NameWrapper>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 10,
                                            },
                                        }}
                                        fullWidth
                                        onChange={this.onChangeFirstName}
                                        defaultValue={this.state.searchCondition.firstName}
                                    />
                                </NameWrapper>
                            </FlexContainerColumn>
                        </LeftRowFlexContainer>
                    </ConditionInputCell>
                    <ConditionButtonCell></ConditionButtonCell>
                </ConditionTableRow>
                <ConditionTableRow>
                    <ConditionColumNameCell>氏名(フリガナ)</ConditionColumNameCell>
                    <ConditionInputCell>
                        <LeftRowFlexContainer>
                            <FlexContainerColumn>
                                <span>セイ</span>
                                <NameWrapper>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 20,
                                            },
                                        }}
                                        fullWidth
                                        onChange={this.onChangeFamilyNameKana}
                                        defaultValue={this.state.searchCondition.familyNameKana}
                                    />
                                </NameWrapper>
                            </FlexContainerColumn>
                            <FlexContainerColumn>
                                <span>メイ</span>
                                <NameWrapper>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 20,
                                            },
                                        }}
                                        fullWidth
                                        onChange={this.onChangeFirstNameKana}
                                        defaultValue={this.state.searchCondition.firstNameKana}
                                    />
                                </NameWrapper>
                            </FlexContainerColumn>
                        </LeftRowFlexContainer>
                    </ConditionInputCell>
                </ConditionTableRow>
                {!needsMaskConfidentialDataByLoginUser() &&
                    <ConditionTableRow>
                        <ConditionColumNameCell>メールアドレス</ConditionColumNameCell>
                        <ConditionInputCell>
                            <PPTextField
                                InputProps={{
                                    inputProps: {
                                        maxLength: 100,
                                    },
                                }}
                                fullWidth
                                onChange={this.onChangeMailAddress}
                                defaultValue={this.state.searchCondition.mailAddress}
                            />
                        </ConditionInputCell>
                    </ConditionTableRow>
                }
                <ConditionTableRow>
                    <ConditionColumNameCell>ステータス</ConditionColumNameCell>
                    <ConditionInputCell colSpan={2}>
                        {this.renderStatusBox()}
                    </ConditionInputCell>
                    <ConditionButtonCell>
                        <AdminDefaultMediumButton
                            onClick={this.onClickSearchButton}>
                            居住者ユーザを検索する
                        </AdminDefaultMediumButton>
                    </ConditionButtonCell>
                </ConditionTableRow>
            </>
        )
    }

    judgeCheckeRoles(roleId: string): boolean {
        if (!this.state.searchCondition || !this.state.searchCondition.roleId) {
            return false
        }
        const roleArray: string[] = this.state.searchCondition.roleId.split(',')
        return roleArray.indexOf(roleId) >= 0
    }

    renderRoleCheckBox(): ReactNode {

        const labels: { label: string, checked: boolean }[] = []

        const roleMaster = ROLE_FOR_RESIDENT.values()
        roleMaster.map((role) => (
            labels.push(
                {
                    label: role.label,
                    checked: this.judgeCheckeRoles(role.value),
                },
            )
        ))

        return (
            <PPCheckboxGroup
                row
                data={labels}
                onChangeChecked={this.clickRole.bind(this)}
            />
        )
    }

    clickRole(label: string, checked: boolean): void {

        const checkedRole = ROLE_FOR_RESIDENT.values().filter(role => {
            return role.label === label
        })

        if (this.state.searchCondition.roleId) {
            const checkedRoleArray: string[] = this.state.searchCondition.roleId.split(',')
            if (checked) {
                checkedRoleArray.push(checkedRole[0].value)
            } else {
                checkedRoleArray.splice(checkedRoleArray.indexOf(checkedRole[0].value), 1)
            }
            const searchCondition = this.state.searchCondition
            searchCondition.roleId = checkedRoleArray.join(',')
            this.setState({searchCondition: searchCondition})
        } else {
            if (checked) {
                const searchCondition = this.state.searchCondition
                searchCondition.roleId = checkedRole[0].value
                this.setState({searchCondition: searchCondition})
            }
        }
    }

    renderStatusBox(): ReactNode {

        const labels: { label: string, checked: boolean }[] = []

        RESIDENT_STATUS.values().forEach((status) => {
            if (RESIDENT_STATUS.HASEKO_WITHDRAW.value !== status.value) {
                labels.push(
                    {
                        label: status.label,
                        checked: this.judgeCheckeStatus(status.value),
                    },
                )
            }
        })

        return (
            <>
                <PPCheckboxGroup
                    row
                    data={labels}
                    onChangeChecked={this.clickStatus.bind(this)}
                />
            </>
        )
    }

    clickStatus(label: string, checked: boolean): void {
        const checkedRole = RESIDENT_STATUS.values().filter(status => {
            return status.label === label
        })

        if (this.state.searchCondition.status) {
            const checkedRoleArray: string[] = this.state.searchCondition.status.split(',')
            if (checked) {
                checkedRoleArray.push(checkedRole[0].value)
            } else {
                checkedRoleArray.splice(checkedRoleArray.indexOf(checkedRole[0].value), 1)
            }
            const searchCondition = this.state.searchCondition
            searchCondition.status = checkedRoleArray.join(',')
            this.setState({searchCondition: searchCondition})
        } else {
            if (checked) {
                const searchCondition = this.state.searchCondition
                searchCondition.status = checkedRole[0].value
                this.setState({searchCondition: searchCondition})
            }
        }
    }


    judgeCheckeStatus(status: string): boolean {

        if (!this.state.searchCondition || !this.state.searchCondition.status) {
            return false
        }
        const roleArray: string[] = this.state.searchCondition.status.split(',')
        return roleArray.indexOf(status) >= 0
    }

    getRidgeList(): OptionProps[] {
        let ridges: OptionProps[] = []
        if (this.state.ridge.details) {
            ridges = this.state.ridge.details.map((ridge) => {
                return ({
                    label: `${ridge.ridgeCd} : ${ridge.ridgeName}`,
                    value: `${ridge.ridgeCd}`,
                })
            })
        }

        return [{
            label: '',
            value: '',
        }, ...ridges]
    }

    onChangeCondominiumGroup(condomoniumGroupId: number): void {
        const searchCondition = this.state.searchCondition
        searchCondition.condominiumGroupId = condomoniumGroupId
        this.setState({searchCondition: searchCondition})
    }

    onChangeCondominiumCd(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.condominiumCd = event.target.value as string
        this.setState({searchCondition: searchCondition})
    }

    onChangeCondominiumName(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.condominiumName = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    onChangeRoomNumber(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.roomNumber = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    onChangeFamilyName(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.familyName = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    onChangeFirstName(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.firstName = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    onChangeFamilyNameKana(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.familyNameKana = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    onChangeFirstNameKana(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.firstNameKana = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    onChangeMailAddress(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.mailAddress = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    onChangeRidgeCd(event: SelectChangeEvent<unknown>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.ridgeCd = event.target.value as string
        this.setState({searchCondition: searchCondition})
    }

    renderResidentUserTable(residentUsers: ResidentUserInfo[]): Nullable<ReactElement> {
        if (residentUsers.length === 0) {
            return null
        }
        const headers: SortableTableHeaderCell[] = []
        headers.push({
            id: 'roomNumber',
            label: '部屋番号',
            sortable: true,
            direction: this.getSortDirection('roomNumber'),
        })
        if (this.administratorMode === ADMINISTORATOR_MODE.ALL.value) {
            headers.push({id: 'condominiumGroupName', label: '物件グループ'})
            headers.push({id: 'condominiumName', label: '物件名'})
        }
        headers.push({id: 'name', label: '氏名'},
            {id: 'adminName', label: '管理者確認用氏名'},
            {id: 'role', label: '権限'},
            {id: 'mailAddress', label: 'メールアドレス'},
            {id: 'parentName', label: 'メインユーザ'},
            {id: 'status', label: 'ステータス'},
            {id: 'updateDatetime', label: '更新日時'},
        )
        return (
            <>
                <Box mt={'10px'} ml={'10px'} pt={'16px'} pb={'16px'}>
                    <IllegalMarginedDivForTablePagination>
                        <BoldSpan>{this.state.searchResult.total}</BoldSpan>件
                        （1〜{this.state.searchResult.total}件目を表示）
                    </IllegalMarginedDivForTablePagination>
                </Box>
                <StyledHr/>
                <Table stickyHeader>
                    <SortableTableHeader
                        noWrap
                        headers={headers}
                        onSortOrderChange={this.handleSortResidentUsers}
                    />
                    <TableBody>
                        {this.state.residentUsers.map(
                            (residentUser, index) => this.renderResidentUserRecord(residentUser, index),
                        )}
                    </TableBody>
                </Table>
            </>
        )
    }

    renderResidentUserRecord(residentUser: ResidentUserInfo, index: number): Nullable<ReactElement> {

        const residentUserStatus = RESIDENT_STATUS.codeToEnumConstants(residentUser.status)
        let residentUserStatusLabel: string = ''
        if (residentUserStatus) {
            residentUserStatusLabel = residentUserStatus.label
        }
        const residentUserDetailUrl: string =
            `/admin/residentUser/${residentUser.condominiumCd}/${residentUser.ridgeCd}/${residentUser.roomNumber}/${residentUser.seq}`
        return (
            <TableRow key={index}>
                <TableCellWordBreakAll align={'center'}>
                    <Link href={residentUserDetailUrl}
                          underline="hover">{residentUser.ridgeName} {residentUser.roomNumber}</Link>
                </TableCellWordBreakAll>
                {this.administratorMode === ADMINISTORATOR_MODE.ALL.value &&
                    <TableCellWordBreakAll align={'center'}>{residentUser.condominiumGroupName}</TableCellWordBreakAll>
                }
                {this.administratorMode === ADMINISTORATOR_MODE.ALL.value &&
                    <TableCellWordBreakAll align={'center'}>{residentUser.condominiumName}</TableCellWordBreakAll>
                }
                <TableCellWordBreakAll align={'center'}>
                    {residentUser.familyName} {residentUser.firstName}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {residentUser.adminFamilyName} {residentUser.adminFirstName}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {residentUser.roleName}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {residentUser.mailAddressPc}
                    {(residentUser.mailAddressPc) ? <br/> : <></>}
                    {residentUser.mailAddressMb}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {residentUser.parentFamilyName} {residentUser.parentFirstName}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {residentUserStatusLabel}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {formatDateTimeForUpdateDate(residentUser.updateDatetime)}
                </TableCellWordBreakAll>
            </TableRow>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return (
            <>

                <ListSearchFormArea
                    searchResult={this.searchResults()}
                    onEnterKey={this.onClickSearchButton}
                >
                    <ConditionTable>
                        <ConditionTableBody>
                            {this.renderSearchConditionCondominiumInfo()}
                            {this.renderSearchConditionResidentInfo()}
                        </ConditionTableBody>
                    </ConditionTable>
                </ListSearchFormArea>
                {this.administratorMode === ADMINISTORATOR_MODE.CONDOMINIUM.value &&
                    <SearchPageActionArea>
                        <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_RESIDENT_USER}>
                            <LeftRowFlexContainer>
                                <AdminDefaultMediumButton onClick={this.registerResidentUser}>
                                    新規
                                </AdminDefaultMediumButton>
                                <Box ml={'15px'}>
                                    <AdminDefaultMediumButton onClick={this.bulkUpdateResidentUser}>
                                        一括更新
                                    </AdminDefaultMediumButton>
                                </Box>
                            </LeftRowFlexContainer>
                        </AuthorityChecker>
                    </SearchPageActionArea>
                }
                <PageMainArea>
                    {this.state.searchResult.limitOverFlag === 0 &&
                        this.renderResidentUserTable(this.state.residentUsers)
                    }
                </PageMainArea>
            </>
        )
    }
}

export default createAdminPage(NatAdminResidentUserListPage)

const TableCellWordBreakAll = styled(TableCell)`
  &.MuiTableCell-root {
    word-break: break-all;
  }
`

const StyledHr = styled.hr`
  border: none;
  height: 1px;
  margin: 0;
  flex-shrink: 0;
  background-color: rgba(0, 0, 0, 0.12);
`

const StyledTableRow = styled(TableRow)`
  white-space: nowrap;
`

const StyledTableCell = styled(TableCell)`
  &.MuiTableCell-root {
    background-color: #ffffff;
  }
`

const IllegalMarginedDivForTablePagination = styled.span`
  left: 0;
  margin-top: -8px;
`

const FlexContainerColumn: StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  &:first-child {
    padding: 0 12px 0 0;
  }
`

const NameWrapper: StyledDiv = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: flex-end;

`
